// Curated editorial placeholder imagery (Unsplash) + static content seeds.
// Phase 8 — clarity-first, conversion-focused package overhaul.
// All Unsplash images. To be replaced by Elizaveta's real work later.

export const SITE = {
    name: "Elizaveta von Stuben Imagery",
    short: "Elizaveta von Stuben",
    wordmark: "Elizaveta von Stuben Imagery",
    cityShort: "Scottsdale, Phoenix & New York",
    cityLong: "Scottsdale · Phoenix · New York · Available for Select Travel Commissions",
    email: "eli@vonstuben.com",
    phone: "", // intentionally hidden until provided
    instagram: "@elivonsky",
    instagramUrl: "https://instagram.com/elivonsky",
    // Homepage primary headline + clarity subhead
    heroHeadline: "Refined Photography & Visual Storytelling for Life, Family, Brands & Business.",
    heroSubhead: "Photography, video, and creative direction for personal portraits, family stories, proposals, private events, brand imagery, and business content in Scottsdale, Phoenix & New York.",
    // Primary brand description (used on About, intro areas)
    brandDescription: "Elizaveta von Stuben Imagery is a refined photography and visual storytelling studio serving Scottsdale and New York. The studio creates polished portraits, family stories, proposals, private events, brand imagery, business content, short films, fine‑art prints, and mentoring.",
    // Shorter version (footer / smaller intros)
    brandDescriptionShort: "Refined photography, video, fine‑art prints, and visual storytelling for personal stories, families, brands, and businesses in Scottsdale, Phoenix & New York.",
    // Shortest variant
    brandDescriptionMicro: "Photography, video, fine‑art prints, and visual storytelling for life, family, brands, and business.",
    tagline: "A Visual Storytelling Studio by Elizaveta von Stuben",
    house: "A visual storytelling studio by Elizaveta von Stuben.",
    // Footer scarcity line — replaces previous "engagements"
    availabilityNote: "A limited number of sessions and commissions are accepted each month.",
    trustLine: "Scottsdale & New York Luxury Photographer · Portraits · Families · Couples · Branding · Business Content · Fine‑Art Prints",
};

// ─── SELECTED CLIENT WORK ────────────────────────────────────────────────
//
// Used by /reviews and /business via <SelectedClientWork /> component.
// Each entry renders as a tile (wordmark by default, monochrome image if
// `logo` URL is supplied).
//
// To add/remove brands: edit this array (kept in FE per Phase 9 spec —
// "keep the strip simple to update from the FE codebase for now"). If
// this needs CMS management later, migrate to a Sanity doc type and swap
// the import in SelectedClientWork.jsx.
//
// Display rules (encoded in the component, not the data):
//   - logo present → render image with `filter: grayscale(100%) opacity(0.7)`
//                    (muted, no brand-color competition).
//   - logo absent  → render typographic wordmark tile (consistent serif
//                    lockup across all tiles). No fake/AI-generated logos.
//
// Order in this array = display order on the page.
export const SELECTED_CLIENTS = [
    { id: 'diageo',         name: 'Diageo' },
    { id: 'db-schenker',    name: 'DB Schenker' },
    { id: 'world-bank',     name: 'World Bank Group' },
    { id: 'nfl',            name: 'NFL' },
    { id: 'grand-seiko',    name: 'Grand Seiko' },
    { id: 'mekhtiyev-law',  name: 'Mekhtiyev Law Firm' },
    { id: 'le-cafe',        name: 'Le Cafe Coffee' },
    { id: 'kirtland-hills', name: 'Kirtland Hills Capital' },
];



export const NAV = [
    { to: "/", label: "Home" },
    { to: "/life-legacy", label: "Life & Legacy", short: "Life & Legacy" },
    { to: "/business", label: "Business" },
    { to: "/portfolio", label: "Portfolio" },
    { to: "/atelier", label: "Atelier" },
    { to: "/mentoring", label: "Mentoring" },
    { to: "/reviews", label: "Reviews" },
];

// Capabilities — favour "Video" over "Motion" for client clarity
export const CAPABILITIES = [
    { n: "01", title: "Creative Direction", desc: "Concept, mood, styling, location, and visual strategy." },
    { n: "02", title: "Editorial Portraiture", desc: "Guided portraits with a cinematic, magazine‑inspired feel." },
    { n: "03", title: "Personal Photography", desc: "Personal portraits, families, couples, proposals, celebrations, and meaningful life moments." },
    { n: "04", title: "Brand Storytelling", desc: "Images that communicate trust, identity, and authority." },
    { n: "05", title: "Professional Headshots", desc: "Polished portraits for LinkedIn, websites, press, and speaking profiles." },
    { n: "06", title: "Website & Social Content", desc: "Visuals for websites, launches, social media, and marketing." },
    { n: "07", title: "Photo + Video", desc: "Short films, reels, brand videos, and highlight films composed with stills." },
    { n: "08", title: "Fine‑Art Prints", desc: "Museum‑inspired prints for homes, offices, and collectors." },
    { n: "09", title: "Signature Retouching", desc: "A refined finish that feels polished but still natural." },
    { n: "10", title: "Visual Mentoring", desc: "Guidance for photographers and creatives building stronger visual identity." },
];

// Process steps — used on Home + studio pages
export const PROCESS_STEPS = [
    { n: "01", t: "Inquire", c: "Share your vision, date, location, and goals." },
    { n: "02", t: "Creative Direction", c: "We plan the mood, styling, location, shot list, and session flow." },
    { n: "03", t: "Photoshoot / Production", c: "You are fully guided through posing, direction, and natural moments." },
    { n: "04", t: "Gallery & Delivery", c: "Receive a polished online gallery with options for prints, albums, and additional selections." },
];

// Reassurance bullets — used in "Why Clients Book" section
export const WHY_BOOK = [
    "You do not need to know how to pose.",
    "Creative direction is included.",
    "Location and styling guidance are included.",
    "Sessions are planned around your goals.",
    "Every gallery is professionally edited.",
    "Photo and video options are available.",
    "Fine‑art prints and albums are available.",
];

// Two studios — clearer descriptions
export const STUDIOS = {
    life: {
        id: "life",
        slug: "/life-legacy",
        name: "Life & Legacy Studio",
        kicker: "Studio One",
        intent: "For your people, your moment, this chapter of your life.",
        tagline:
            "Personal portraits, family stories, couples, proposals, private celebrations, events, and meaningful life moments.",
        long:
            "Refined personal photography for the people, relationships, and moments that matter most. Every session is fully guided — posing, styling, and creative direction included — so the experience feels calm and unmistakably yours.",
        services: [
            { id: "personal-portraits", title: "Personal Portraits", copy: "Polished personal portraits that look like you at your highest level." },
            { id: "family-stories", title: "Family Stories", copy: "An afternoon designed around the people you love most." },
            { id: "couples", title: "Couples", copy: "Tender, restrained, and entirely true to the two of you." },
            { id: "proposals", title: "Proposals & Engagements", copy: "The moment, held still — discreetly composed and beautifully kept." },
            { id: "celebrations", title: "Private Celebrations & Events", copy: "Intimate gatherings, ceremonies, and quiet milestones." },
            { id: "moments", title: "Meaningful Life Moments", copy: "Maternity, motherhood, graduation, birthdays, anniversaries, and personal milestones." },
        ],
        ctaPrimary: "Begin a Life & Legacy session",
        ctaSecondary: "View the Life & Legacy portfolio",
        heroImage: "https://res.cloudinary.com/dub8rhyez/image/upload/f_auto,q_auto,w_2400/_portfolio_05182023_2-24_nqi4b7.jpg",
        accent: "warm-ivory",
    },
    brand: {
        id: "brand",
        slug: "/brand-business",
        name: "Brand & Business Studio",
        kicker: "Studio Two",
        intent: "Photography and video for founders, professionals, teams, and businesses who need to look trusted, polished, and ready to sell.",
        tagline: "Professional portraits, team headshots, brand imagery, event coverage, and hospitality content — plus photo + video — for founders, medical and hospitality brands, professional services, event organizers, and growing businesses.",
        long:
            "Practical, premium visuals built for how businesses actually operate. Every session is planned around your goals — what your clients need to see, where the images will live, and the credibility you need to build before the first sales call.",
        services: [
            { id: "headshots", title: "Professional Headshots", copy: "Polished portraits for LinkedIn, websites, press kits, and speaking profiles." },
            { id: "brand-story", title: "Brand Story Session", copy: "Stronger website and social media imagery for founders, entrepreneurs, creatives, and service providers." },
            { id: "photo-video", title: "Photo + Video Content", copy: "Photography and short‑form video for websites, launches, ads, and social media." },
            { id: "content-day", title: "Business Content Day", copy: "A focused production day that creates a complete content library." },
            { id: "team", title: "Team Headshots", copy: "Consistent, professional portraits for companies, teams, and organizations." },
            { id: "content-support", title: "Content Support / Retainers", copy: "Recurring monthly or quarterly content for businesses that need ongoing visuals." },
        ],
        ctaPrimary: "Build Your Brand Presence",
        ctaSecondary: "View Business Portfolio",
        heroImage: "https://res.cloudinary.com/dub8rhyez/image/upload/f_auto,q_auto,w_2400/01_Corporate_24_nzlgwo.jpg",
        accent: "ink",
    },
};

const U = (id, w = 1600) => {
    // Backward-compat: every legacy U("photo-...") call now resolves to a
    // Cloudinary asset from Elizaveta's portfolio. Mapping is deterministic
    // (hash of the seed) so cards keep stable identities across renders.
    let h = 0;
    const s = String(id);
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
    const pool = CLD_PORTRAIT_POOL;
    return cld(pool[Math.abs(h) % pool.length], `f_auto,q_auto,w_${w}`);
};

// Business / corporate / event imagery pool — used for Brand & Business
// portfolio items and the brand studio hero.
const UB = (id, w = 1600) => {
    let h = 0;
    const s = String(id);
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
    return cld(CLD_BUSINESS_POOL[Math.abs(h) % CLD_BUSINESS_POOL.length], `f_auto,q_auto,w_${w}`);
};

// Approved Cloudinary asset pools — Elizaveta's portfolio.
const CLD_PORTRAIT_POOL = [
    "_portfolio_Patrick_Alston-21_pbsi5k.jpg",
    "Lloyd-3-sm_u2nehv.jpg",
    "_portfolio_Taylor_4_39_sm_gmpecx.jpg",
    "_portfolio_05182023_2-24_nqi4b7.jpg",
    "_portfolio_983A9638_vifhxw.jpg",
    "_portfolio_Larry_day1_rowe-127_w6fqwo.jpg",
    "_portfolio_Xiaowen-31_s3kk50.jpg",
    "_portfolio_Xiaowen-9_mqijyq.jpg",
    "_portfolio_983A7416_ljouaj.jpg",
    "_portfolio_983A9641_bea5w5.jpg",
    "_portfolio_Mojo_10_f3jzm4.jpg",
    "_portfolio_07052023-32_x5qwpq.jpg",
    "_portfolio_983A7274_v3jub7.jpg",
    "_portfolio_12202022_Dumbo-44_lck8xt.jpg",
    "v1778617498/983A4111_bbz0e7.jpg",
];

const CLD_BUSINESS_POOL = [
    "3231-306_Corporate_26_ermdkt.jpg",
    "04_Corporate_28_xx7y2a.jpg",
    "02_Corporate_19_sgdfng.jpg",
    "01_Corporate_24_nzlgwo.jpg",
    "06_Corporate_25_ryvvp9.jpg",
    "3061-343_Corporate_14_vl0ngj.jpg",
    "03_Corporate_13_o16cwq.jpg",
    "3363-653_Corporate_9_kwj8fm.jpg",
    "3831-343_Corporate_1_cdckah.jpg",
    "Business_portfolio_evs-1_qpkkux.jpg",
    "05_Corporate_10_jsr2ba.jpg",
    "Business_portfolio_evs-2_o6h085.jpg",
    "6364-336_Corporate_6_jghese.jpg",
    "3832-323_Corporate_8_wqlfip.jpg",
    "3036-316_Corporate_23_tftw0g.jpg",
    "3632-646_Corporate_18_or8zqh.jpg",
    "_____Businesscreative_portfolio_evs-7_kykjzs.jpg",
];

// Landscapes / cities / travel — exclusively for The Atelier travel archive,
// fine-art landscape collections, and the Atelier hero. No portraits here.
const CLD_LANDSCAPES_AND_CITIES_POOL = [
    "vonstuben/Landscapes_and_Cities/6164-326_Portfolio_website-2-",
    "vonstuben/Landscapes_and_Cities/3163-333_NY_portfolio-1",
    "vonstuben/Landscapes_and_Cities/6637-353_Japan_diff_portfolio",
    "vonstuben/Landscapes_and_Cities/6564-313_Portfolio_website-2-",
    "vonstuben/Landscapes_and_Cities/3838-336_Portfolio_website-1-",
    "vonstuben/Landscapes_and_Cities/3236-393_Portfolio_website-1-",
    "vonstuben/Landscapes_and_Cities/3536-353_Portfolio_website-24",
    "vonstuben/Landscapes_and_Cities/6164-613_Portfolio_website-3",
    "vonstuben/Landscapes_and_Cities/6636-303_Portfolio_website-1-",
    "vonstuben/Landscapes_and_Cities/3237-616_Portfolio_website-1-",
    "vonstuben/Landscapes_and_Cities/6435-343_Portfolio_website-21",
    "vonstuben/Landscapes_and_Cities/3565-363_Portfolio_website-19",
    "vonstuben/Landscapes_and_Cities/3765-333_Portfolio_website-4-",
    "vonstuben/Landscapes_and_Cities/6535-333_Portfolio_website-3-",
    "vonstuben/Landscapes_and_Cities/3166-326_Portfolio_website-1-",
    "vonstuben/Landscapes_and_Cities/6136-313_Portfolio_website-6",
    "vonstuben/Landscapes_and_Cities/3535-653_Portfolio_website-8",
    "vonstuben/Landscapes_and_Cities/3461-666_Dagestan_landscape",
];

// Signed fine-art editions — used exclusively on print shop cards and
// print detail pages. Mix of NY, Bryant Park, Kalmykia, and Japan series.
const CLD_ART_PRINTS_POOL = [
    "vonstuben/Art%20Prints/6439-363_NY-sm-prints",
    "vonstuben/Art%20Prints/6531-323_EP_prints_-_10",
    "vonstuben/Art%20Prints/3439-313_NY-sm-prints---6",
    "vonstuben/Art%20Prints/3931-386_NY-sm-prints---1",
    "vonstuben/Art%20Prints/6531-643_NY-sm-prints-1",
    "vonstuben/Art%20Prints/6437-613_NY-sm-prints---5",
    "vonstuben/Art%20Prints/6338-623_NY-sm-prints---8",
    "vonstuben/Art%20Prints/6166-366_NY-sm-prints---2",
    "vonstuben/Art%20Prints/3861-396_NY-sm-prints---4",
    "vonstuben/Art%20Prints/6239-376_EP-prints-Bryant_Par",
    "vonstuben/Art%20Prints/3135-613_Kalmykia-Nomad-Art",
    "vonstuben/Art%20Prints/3363-623_Japan_Fine_Art_-_3",
    "vonstuben/Art%20Prints/3966-306_Japan_Fine_Art_-_14",
    "vonstuben/Art%20Prints/3762-376_Japan_Fine_Art_-_7",
    "vonstuben/Art%20Prints/3766-613_Japan_Fine_Art_-_12",
    "vonstuben/Art%20Prints/3230-666_Japan_Fine_Art_-_6",
    "vonstuben/Art%20Prints/3066-613_Japan_Fine_Art_-_8",
    "vonstuben/Art%20Prints/3532-313_Japan_Fine_Art_-_10",
    "vonstuben/Art%20Prints/6330-623_Japan_Fine_Art_-_9",
    "vonstuben/Art%20Prints/3639-346_Japan_Fine_Art_-_5",
    "vonstuben/Art%20Prints/3036-633_Japan_Fine_Art_-_2",
    "vonstuben/Art%20Prints/3336-643_Japan_Fine_Art_-_4",
    "vonstuben/Art%20Prints/3237-356_Japan_Fine_Art_-_1",
];

// Pick deterministically from the landscape / city travel pool.
const LA = (id, w = 1600) => cld(
    CLD_LANDSCAPES_AND_CITIES_POOL[idxFromSeed(id, CLD_LANDSCAPES_AND_CITIES_POOL.length)],
    `f_auto,q_auto,w_${w}`
);

// Pick deterministically from the fine-art print pool.
const AP = (id, w = 1600) => cld(
    CLD_ART_PRINTS_POOL[idxFromSeed(id, CLD_ART_PRINTS_POOL.length)],
    `f_auto,q_auto,w_${w}`
);

function idxFromSeed(seed, mod) {
    let h = 0;
    const s = String(seed);
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
    return Math.abs(h) % mod;
}

// ===========================================================
// CLOUDINARY HELPERS
// Cloud: dub8rhyez · Used for the Lifestyle Portraits collection.
// ===========================================================
const CLOUDINARY_CLOUD = "dub8rhyez";

/**
 * Build a Cloudinary delivery URL for a given publicId (including any
 * version prefix like v17786... and file extension) with optional transform.
 * Defaults to f_auto,q_auto for best quality/perf balance.
 */
export const cld = (publicId, transform = "f_auto,q_auto,w_1400") =>
    `https://res.cloudinary.com/${CLOUDINARY_CLOUD}/image/upload/${transform}/${publicId}`;

/**
 * Build a srcSet for responsive image delivery from a Cloudinary publicId.
 * Widths target common breakpoints; the browser picks the smallest needed.
 */
export const cldSrcSet = (publicId) => {
    const widths = [600, 900, 1200, 1600, 2000];
    return widths
        .map((w) => `${cld(publicId, `f_auto,q_auto,w_${w}`)} ${w}w`)
        .join(", ");
};

// ----- LIFESTYLE PORTRAITS (Cloudinary collection) -----
// Curated by Elizaveta. Placed under Life & Legacy · Personal Portraits.
// Each item connects into PORTFOLIO via category "Portraits" + lifeTags.
export const LIFESTYLE_PORTRAITS = [
    { id: "lp01", publicId: "v1778617498/983A4111_bbz0e7.jpg",                       title: "Studio Stillness",          location: "Studio",       featured: true  },
    { id: "lp02", publicId: "v1778617497/Lloyd-3-sm_u2nehv.jpg",                     title: "Lloyd",                     location: "Editorial",    featured: true  },
    { id: "lp03", publicId: "v1778617497/_portfolio_05182023_2-24_nqi4b7.jpg",       title: "Afternoon Light",            location: "New York",     featured: true  },
    { id: "lp04", publicId: "v1778617497/_portfolio_07052023-32_x5qwpq.jpg",         title: "Summer Frame",               location: "New York" },
    { id: "lp05", publicId: "v1778617497/_portfolio_12202022_Dumbo-44_lck8xt.jpg",   title: "Dumbo, Late Afternoon",      location: "Brooklyn",     featured: true  },
    { id: "lp06", publicId: "v1778617497/_portfolio_983A7274_v3jub7.jpg",            title: "Quiet Direction"  },
    { id: "lp07", publicId: "v1778617497/_portfolio_983A7416_ljouaj.jpg",            title: "Soft Window"  },
    { id: "lp08", publicId: "v1778617497/_portfolio_983A9638_vifhxw.jpg",            title: "Held Light"  },
    { id: "lp09", publicId: "v1778617496/_portfolio_983A9641_bea5w5.jpg",            title: "A Slow Glance"  },
    { id: "lp10", publicId: "v1778617496/_portfolio_Larry_day1_rowe-127_w6fqwo.jpg", title: "Larry, Day One",             location: "Editorial",    featured: true  },
    { id: "lp11", publicId: "v1778617496/_portfolio_Mojo_10_f3jzm4.jpg",             title: "Mojo, In Repose"  },
    { id: "lp12", publicId: "v1778617496/_portfolio_Patrick_Alston-21_pbsi5k.jpg",   title: "Patrick Alston, Studio",     location: "Studio",       featured: true  },
    { id: "lp13", publicId: "v1778617497/_portfolio_Taylor_4_39_sm_gmpecx.jpg",      title: "Taylor"  },
    { id: "lp14", publicId: "v1778617497/_portfolio_Xiaowen-31_s3kk50.jpg",          title: "Xiaowen, I"  },
    { id: "lp15", publicId: "v1778617496/_portfolio_Xiaowen-9_mqijyq.jpg",           title: "Xiaowen, II" },
].map((p) => ({
    ...p,
    category: "Portraits",
    lifeTags: ["Personal Portraits"],
    source: "Cloudinary",
    alt: `Lifestyle portrait by Elizaveta von Stuben Imagery in Scottsdale, Phoenix & New York${p.location ? ` — ${p.location}` : ""}`,
    image: cld(p.publicId, "f_auto,q_auto,w_900"),
    imageLarge: cld(p.publicId, "f_auto,q_auto,w_1800"),
    imageHome: cld(p.publicId, "f_auto,q_auto,w_1400"),
    srcSet: cldSrcSet(p.publicId),
}))
    // Featured images first so they lead the Life & Legacy archive
    // and dominate the homepage curated preview.
    .sort((a, b) => Number(!!b.featured) - Number(!!a.featured));

// ----- HOMEPAGE HERO -----
// Curated by Elizaveta. Single hero asset, delivered via Cloudinary with
// f_auto, q_auto, and responsive widths. The srcSet lets the browser pick
// the smallest variant that fits the viewport while a 2400w "default" src
// keeps large desktops sharp.
const HERO_PUBLIC_ID = "v1778621135/vonstuben_mainpage_hi27kb.jpg";
export const HERO_IMAGE = cld(HERO_PUBLIC_ID, "f_auto,q_auto,w_2400");
export const HERO_IMAGE_MOBILE = cld(HERO_PUBLIC_ID, "f_auto,q_auto,w_1200");
export const HERO_IMAGE_SRCSET = [
    `${cld(HERO_PUBLIC_ID, "f_auto,q_auto,w_900")} 900w`,
    `${cld(HERO_PUBLIC_ID, "f_auto,q_auto,w_1400")} 1400w`,
    `${cld(HERO_PUBLIC_ID, "f_auto,q_auto,w_2000")} 2000w`,
    `${cld(HERO_PUBLIC_ID, "f_auto,q_auto,w_2400")} 2400w`,
    `${cld(HERO_PUBLIC_ID, "f_auto,q_auto,w_3000")} 3000w`,
].join(", ");

export const ABOUT_IMAGE = U("photo-1488426862026-3ee34a7d66df", 1400);
export const HERO_FALLBACK = U("photo-1488161628813-04466f872be2", 2000);

export const CATEGORIES = [
    "All",
    "Portraits",
    "Family",
    "Branding",
    "Events",
    "Business",
    "Editorial",
];

// Per-scope category sets for the segmented portfolio pages
export const SCOPE_CATEGORIES = {
    "life-legacy": [
        "All",
        "Personal Portraits",
        "Family Stories",
        "Couples",
        "Proposals & Engagements",
        "Private Celebrations",
        "Meaningful Life Moments",
    ],
    "brand-business": [
        "All",
        "Professional Headshots",
        "Founder Portraits",
        "Personal Branding",
        "Team Headshots",
        "Website Content",
        "Social Media Content",
        "Product / Service Details",
        "Photo + Video",
    ],
};

export const SCOPE_FILTER = {
    "life-legacy": (item) => ["Portraits", "Family", "Events"].includes(item.category),
    // Brand & Business: must explicitly carry brandTags. Prevents stray
    // "Editorial" items tagged with lifeTags from leaking into business views.
    "brand-business": (item) => Array.isArray(item.brandTags) && item.brandTags.length > 0,
};

export const SCOPE_SUBFILTER = {
    "life-legacy": (item, label) =>
        label === "All" ? true : Array.isArray(item.lifeTags) && item.lifeTags.includes(label),
    "brand-business": (item, label) =>
        label === "All" ? true : Array.isArray(item.brandTags) && item.brandTags.includes(label),
};

export const SCOPE_META = {
    "life-legacy": {
        slug: "/portfolio/life-legacy",
        kicker: "Life & Legacy · Portfolio",
        title: "Personal portraits, family stories, couples, proposals, and meaningful life moments.",
        copy: "A curated archive from the Life & Legacy Studio — personal portraits, family stories, couples, proposals and engagements, private celebrations, and meaningful life moments.",
        ctaBackLabel: "Back to Life & Legacy Studio",
        ctaBackTo: "/life-legacy",
        ctaRequestType: "Story Session",
    },
    "brand-business": {
        slug: "/portfolio/brand-business",
        kicker: "Brand & Business · Portfolio",
        title: "Visuals that make founders, professionals & brands look trusted, premium, and ready to sell.",
        copy: "Professional headshots, founder portraits, personal branding, team headshots, website content, social media content, product and service details, and photo + video work.",
        ctaBackLabel: "Back to Brand & Business Studio",
        ctaBackTo: "/brand-business",
        ctaRequestType: "Brand Story Session",
    },
    "all": {
        slug: "/portfolio",
        kicker: "Portfolio",
        // Phase 13 (A7) — main /portfolio headline swap. Scoped routes
        // /portfolio/life-legacy and /portfolio/brand-business keep their
        // scope-specific titles above; only the unfiltered "all" view's
        // primary heading reads as the social-proof line below the kicker.
        title: "A few of the brands, teams, and clients who trusted the process.",
        copy: "Personal portraits, family stories, couples, proposals, private celebrations, branding, business content, and creative editorials. Each image is composed with intention — light, gesture, and presence held still.",
        ctaRequestType: "",
    },
};

const PORTFOLIO_PLACEHOLDERS = [
    { id: "p1", category: "Portraits", lifeTags: ["Personal Portraits"], title: "Quiet Confidence", image: U("photo-1531746020798-e6953c6e8e04") },
    { id: "p2", category: "Portraits", lifeTags: ["Personal Portraits"], title: "Linen & Light", image: U("photo-1524504388940-b1c1722653e1") },
    { id: "p3", category: "Family", lifeTags: ["Meaningful Life Moments"], title: "The Becoming", image: U("photo-1581952976147-5a2d15560349") },
    { id: "p4", category: "Family", lifeTags: ["Meaningful Life Moments"], title: "Soft Architecture", image: U("photo-1555252333-9f8e92e65df9") },
    { id: "p5", category: "Family", lifeTags: ["Family Stories"], title: "Sunday Morning", image: U("photo-1511895426328-dc8714191300") },
    { id: "p6", category: "Family", lifeTags: ["Family Stories"], title: "At the Threshold", image: U("photo-1543342384-1f1350e27861") },
    { id: "p7", category: "Branding", brandTags: ["Founder Portraits", "Personal Branding"], title: "Atelier Founder", image: UB("p7") },
    { id: "p8", category: "Branding", brandTags: ["Personal Branding", "Website Content"], title: "Studio Practice", image: UB("p8") },
    { id: "p9", category: "Editorial", brandTags: ["Product / Service Details", "Social Media Content"], title: "Marble & Wool", image: UB("p9") },
    { id: "p10", category: "Editorial", brandTags: ["Social Media Content", "Website Content"], title: "Cover Story", image: UB("p10") },
    { id: "p11", category: "Events", lifeTags: ["Private Celebrations", "Proposals & Engagements"], title: "A Private Ceremony", image: UB("p11") },
    { id: "p12", category: "Events", lifeTags: ["Private Celebrations"], title: "Dusk Reception", image: UB("p12") },
    { id: "p13", category: "Business", brandTags: ["Professional Headshots", "Founder Portraits"], title: "Founder Headshot", image: UB("p13") },
    { id: "p14", category: "Business", brandTags: ["Personal Branding", "Website Content", "Photo + Video"], title: "At the Desk", image: UB("p14") },
    { id: "p15", category: "Portraits", lifeTags: ["Couples"], title: "After Hours", image: U("photo-1488161628813-04466f872be2") },
    { id: "p16", category: "Editorial", lifeTags: ["Meaningful Life Moments"], title: "The Long Pause", image: U("photo-1521577352947-9bb58764b69a") },
];

// Real lifestyle portraits (Cloudinary) lead the Life & Legacy archive,
// followed by remaining placeholders that fit other Life & Legacy
// subcategories or sit inside Brand & Business.
export const PORTFOLIO = [
    ...LIFESTYLE_PORTRAITS,
    ...PORTFOLIO_PLACEHOLDERS,
];

// Legacy packages array — preserved for compatibility (Home.jsx may reference it elsewhere)
export const PACKAGES = [];

export const VIDEO_REELS = [
    { id: "v1", title: "Atelier — Brand Film", thumb: U("photo-1492447166138-50c3889fccb1"), kind: "Film", duration: "01:42" },
    { id: "v2", title: "Family — The Becoming", thumb: U("photo-1581952976147-5a2d15560349"), kind: "Film", duration: "00:58" },
    { id: "v3", title: "Founder Story — Studio", thumb: U("photo-1521572163474-6864f9cf17ab"), kind: "Film", duration: "02:11" },
    { id: "v4", title: "Editorial Reel", thumb: U("photo-1488161628813-04466f872be2"), kind: "Reel", duration: "00:22" },
    { id: "v5", title: "Family Reel", thumb: U("photo-1511895426328-dc8714191300"), kind: "Reel", duration: "00:30" },
    { id: "v6", title: "Wedding Reel — Sonoran", thumb: U("photo-1519225421980-715cb0215aed"), kind: "Reel", duration: "00:28" },
];

export const PRINTS = [
    { id: "pr1", title: "Desert Nomad, Kalmykia",       edition: "Edition of 12", collection: "Kalmykia · Limited Edition",     statement: "A nomad and his camel against the open desert — a quiet study of solitude, scale, and a vanishing way of life.", image: AP("pr1"), startingAt: "$650" },
    { id: "pr2", title: "Forest Deer, Japan",           edition: "Edition of 10", collection: "Japan Series · Limited Edition", statement: "A deer at rest in the forests of Nara — composed of stillness, moss, and filtered light.",                       image: AP("pr2"), startingAt: "$720" },
    { id: "pr3", title: "Temple Light, Japan",          edition: "Edition of 10", collection: "Japan Series · Limited Edition", statement: "A monk passes through temple shadow — saffron robes carrying centuries of quiet practice.",                  image: AP("pr3"), startingAt: "$780" },
    { id: "pr4", title: "Threshold, Japan",             edition: "Open Edition",  collection: "Japan Series · Open Edition",    statement: "A doorway, a footstep, a held breath — the architecture of arrival in old Japan.",                          image: AP("pr4"), startingAt: "$540" },
    { id: "pr5", title: "Snow Monkey, Japan",           edition: "Edition of 6",  collection: "Japan Series · Limited Edition", statement: "A solitary macaque in winter water — patience photographed at the temperature of stillness.",               image: AP("pr5"), startingAt: "$880" },
    { id: "pr6", title: "Lake Reflection, Japan",       edition: "Open Edition",  collection: "Japan Series · Open Edition",    statement: "Water as a second sky — mountains repeating themselves softly along the shoreline.",                         image: AP("pr6"), startingAt: "$720" },
    { id: "pr7", title: "Quiet Path, Japan",            edition: "Edition of 8",  collection: "Japan Series · Limited Edition", statement: "A path through cedar and stone — a walk taken slowly enough to remember.",                                image: AP("pr7"), startingAt: "$650" },
    { id: "pr8", title: "Bryant Park Study, New York",  edition: "Open Edition",  collection: "New York Series · Open Edition", statement: "Manhattan composing itself through bare winter branches — a quiet pause in the middle of the city.",       image: AP("pr8"), startingAt: "$620" },
];

export const PRINT_SIZES = [
    "11\" x 14\"",
    "16\" x 20\"",
    "24\" x 30\"",
    "30\" x 40\"",
    "40\" x 50\"",
];

export const PRINT_PAPERS = [
    "Fine Art Matte (Hahnemühle Photo Rag)",
    "Semi‑Gloss Archival",
    "Cotton Baryta",
];

export const PRINT_FRAMES = [
    "Unframed",
    "Natural Oak",
    "Walnut",
    "Matte Black",
    "Antique Brass (custom)",
];

// Mentoring (Studio School) tiers
export const MENTORING_OFFERS = [
    {
        id: "creative-direction",
        title: "Creative Direction Mentoring",
        for: "For photographers developing taste, storytelling, posing, and visual identity.",
        format: "4 private 1:1 sessions · 90 min each",
        includes: ["Portfolio review", "Posing & direction frameworks", "Editing taste & color philosophy", "Voice + visual identity"],
    },
    {
        id: "business",
        title: "Photography Business Mentoring",
        for: "For photographers booking higher‑value clients with a more profitable brand.",
        format: "6 private 1:1 sessions · 60 min each",
        includes: ["Pricing & packages", "Sales & client experience", "Marketing systems", "Portfolio strategy"],
    },
    {
        id: "content",
        title: "Content & Personal Brand Workshop",
        for: "For founders & creatives creating stronger, more intentional content.",
        format: "Half‑day workshop · in person or virtual",
        includes: ["Visual identity audit", "Content pillars", "Self‑direction techniques", "Editing & curation"],
    },
];

export const REVIEWS = [
    { id: "r1", quote: "She made me feel confident and guided me through every frame. The photos look like a magazine.", name: "Anastasia M.", type: "Brand Story Session" },
    { id: "r2", quote: "Working with Elizaveta felt calm, professional, and high‑end — every detail considered, every image intentional.", name: "The Hölzer Family", type: "Family Story Session" },
    { id: "r3", quote: "Our brand finally looks the way it felt in my head. The images reframed how clients perceive us.", name: "Liana K., Founder — Maison Andra", type: "Business Content Day" },
    { id: "r4", quote: "I do not love being photographed. Somehow, she made it the easiest hour of my year.", name: "Daniel W.", type: "Personal Portrait Session" },
    { id: "r5", quote: "We will treasure these images forever. They are not photographs. They are heirlooms.", name: "Sara & Theo", type: "Story Session" },
    { id: "r6", quote: "The experience was concierge from beginning to end. Our team felt seen and styled, not directed.", name: "René P., CEO", type: "Team Headshots" },
];

// Inquire dropdown options — clarity-focused
export const SESSION_TYPES = [
    // Summer promo session types — keyed by the /business package CTAs so a
    // visitor arriving at /inquire?type=... has the right option already
    // selected. Listed first because they're time-limited and the most
    // common arrival path from /business during the promo window.
    "Summer Headshot + Brand Refresh",
    "Summer Brand Content Day",
    "Summer Brand + Video Day",
    "Personal Portrait Session",
    "Story Session",
    "Proposal / Engagement",
    "Family / Couples",
    "Private Event / Celebration",
    "Professional Headshots",
    "Brand Story Session",
    "Photo + Video Content",
    "Business Content Day",
    "Team Headshots",
    "Fine-Art Prints",
    "Mentoring",
    "Not sure yet",
];

// Location options for the inquiry form dropdown
export const LOCATION_OPTIONS = [
    "Scottsdale / Phoenix",
    "New York",
    "Studio",
    "Outdoor",
    "Client location",
    "Destination / Travel",
    "Not sure yet",
];

// Estimated investment range (renamed from "budget") — approachable tiers
export const BUDGETS = [
    "Under $500",
    "$500–$750",
    "$750–$1,200",
    "$1,200–$2,500",
    "$2,500+",
    "Not sure yet",
];

export const HEAR_ABOUT = [
    "Instagram",
    "Referral",
    "Google",
    "Press / Editorial",
    "Other",
];


// ===========================================================
// PRICING ARCHITECTURE — Clarity-first, conversion-focused.
// Strategy: Entry · Most Popular · Premium · Custom · Add-ons.
// "Starting at" pricing, never discount language.
// ===========================================================

export const PRICING_REASSURANCE =
    "You do not need to know how to pose or plan everything yourself. Every session includes guidance, direction, and a clear creative process from inquiry to final delivery.";

export const PRICING_CUSTOM_NOTE =
    "Every session is custom quoted based on creative direction, location, timing, usage, final deliverables, and whether photography, video, prints, or albums are included.";

export const PRICING_BOOKING_NOTE =
    "To reserve a session, clients complete the inquiry form. Once details are confirmed, a photography agreement and invoice are sent. The date is officially secured once the agreement is signed and the deposit is paid.";

// ----- LIFE & LEGACY PACKAGES (4 tiers) -----
export const LIFE_LEGACY_PACKAGES = [
    {
        id: "personal-portrait",
        name: "Personal Portrait Session",
        starting: "Starting at $450",
        timing: "Up to 45 minutes",
        bestFor: "Personal portraits, couples, creative portraits, simple lifestyle sessions, graduation, motherhood, and individual milestones.",
        positioning: "A simple, polished, guided session for beautiful personal images without a large production.",
        includes: [
            "Up to 45 minutes of photography",
            "One location or simple studio setup",
            "One outfit / simple look",
            "Posing guidance",
            "Professionally edited online gallery",
            "15+ edited images included",
            "Option to add extra images, prints, albums, or motion",
        ],
        cta: "Request Availability",
        sessionType: "Personal Portrait Session",
    },
    {
        id: "story-session",
        name: "Story Session",
        starting: "Starting at $550",
        timing: "Up to 60 minutes",
        badge: "Most Popular",
        bestFor: "Family stories, couples, proposals, engagement photos, small celebrations, and meaningful life moments.",
        positioning: "The best choice for clients who want more time, more variety, and a fuller story from their session.",
        includes: [
            "Up to 60 minutes of photography",
            "Location guidance",
            "1–2 looks or simple scene variety",
            "Posing and natural‑moment direction",
            "Professionally edited online gallery",
            "25+ edited images included",
            "Fine‑art print options available",
        ],
        cta: "Create Your Story Session",
        sessionType: "Story Session",
    },
    {
        id: "signature-story",
        name: "Signature Story Session",
        starting: "Starting at $750",
        timing: "Up to 90 minutes",
        bestFor: "Clients who want more variety, multiple looks, a stronger visual concept, or a more elevated personal session.",
        positioning: "A more intentional experience for portraits, family, couples, proposals, celebrations, or personal storytelling.",
        includes: [
            "Up to 90 minutes of photography",
            "Creative planning",
            "Multiple looks or nearby scenes",
            "Location or studio planning",
            "Professionally edited gallery",
            "40+ edited images included",
            "Print and album options available",
        ],
        cta: "Plan Your Story Session",
        sessionType: "Story Session",
    },
    {
        id: "custom-celebration",
        name: "Custom Celebration / Event Coverage",
        starting: "Starting at $950",
        timing: "Custom quoted based on scope",
        bestFor: "Private events, birthdays, proposals, extended family sessions, intimate celebrations, multi‑location shoots, or custom concepts.",
        positioning: "Custom coverage designed around the people, story, location, timing, and final use.",
        includes: [
            "Custom quote based on scope",
            "Extended coverage",
            "Multiple locations or scenes if needed",
            "Photo and video options",
            "Premium retouching",
            "Fine‑art prints or albums available",
        ],
        cta: "Request a Custom Quote",
        sessionType: "Private Event / Celebration",
    },
];

// ----- LIFE & LEGACY ADD-ONS (Add Motion to Your Story) -----
export const LIFE_LEGACY_ADDONS = [
    {
        id: "social-reel",
        name: "Social Reel Add‑On",
        starting: "Starting at $250",
        bestFor: "Short vertical clips, behind‑the‑scenes, proposal moments, family memories, and social media.",
        sessionType: "Photo + Video Content",
    },
    {
        id: "highlight-film",
        name: "Highlight Film",
        starting: "Starting at $550",
        bestFor: "Proposals, celebrations, family stories, and emotional short films.",
        sessionType: "Photo + Video Content",
    },
    {
        id: "photo-film-story",
        name: "Photo + Film Story Session",
        starting: "Starting at $950",
        bestFor: "Clients who want both photography and a short cinematic film from the same personal session.",
        sessionType: "Photo + Video Content",
    },
];

// ----- BRAND & BUSINESS PACKAGES (4 main tiers) -----
export const BRAND_BUSINESS_PACKAGES = [
    {
        id: "professional-headshot",
        name: "Professional Headshot Session",
        starting: "Starting at $375",
        timing: "Up to 30 minutes",
        bestFor: "Polished portraits for LinkedIn, websites, press, speaking profiles, and professional credibility.",
        positioning: "Clean, professional portraits that immediately build trust with prospective clients.",
        includes: [
            "Up to 30 minutes",
            "Studio or on‑location option",
            "Posing and expression guidance",
            "Clean professional lighting",
            "Online proofing gallery",
            "3 professionally retouched final images",
            "Additional retouched images available for purchase",
        ],
        cta: "Book Professional Portraits",
        sessionType: "Professional Headshots",
    },
    {
        id: "team-headshot-day",
        name: "Team Headshot Day",
        starting: "From $750",
        timing: "90 minutes · up to 5 people",
        bestFor: "Medical practices, professional services, hospitality teams, and growing companies who want every staff member to look like part of the same brand.",
        positioning: "Consistent, professional portraits for the whole team, captured on‑site.",
        includes: [
            "On‑site setup at your office, clinic, or venue",
            "Consistent lighting and background across the team",
            "Posing and expression guidance for every person",
            "Online proofing gallery",
            "Professionally retouched final selects",
            "Tiered scaling: $1,200 — 4 hours · up to 10 people",
            "$1,800 — half day · up to 18 people",
            "$2,800 — full day · up to 30 people",
        ],
        cta: "Plan a Team Headshot Day",
        sessionType: "Team Headshots",
    },
    {
        id: "brand-story",
        name: "Brand Story Session",
        starting: "Starting at $750",
        timing: "Up to 75 minutes",
        badge: "Most Popular",
        bestFor: "Founders, entrepreneurs, creatives, and service providers who need stronger website and social media imagery.",
        positioning: "A guided session that creates the trust‑building images your business has been missing.",
        includes: [
            "Brand direction call",
            "Shot list planning",
            "Founder / personal brand portraits",
            "Lifestyle brand imagery",
            "Detail / working shots",
            "Professionally edited online gallery",
            "25+ edited images included",
            "Content‑ready images for website and social media",
        ],
        cta: "Build Your Brand Presence",
        sessionType: "Brand Story Session",
    },
    {
        id: "photo-video-content",
        name: "Photo + Video Content Session",
        starting: "Starting at $1,200",
        timing: "Up to 2 hours",
        bestFor: "Businesses that need both strong photos and simple video content for marketing, launches, websites, and social media.",
        positioning: "Photography and short‑form video produced in one session — ready to ship.",
        includes: [
            "Planning call",
            "Shot list",
            "Brand portraits",
            "Lifestyle / content imagery",
            "Short‑form video clips or reel‑style content",
            "Edited photo gallery",
            "30+ edited images included",
            "Social media‑ready deliverables",
        ],
        cta: "Create Photo + Video Content",
        sessionType: "Photo + Video Content",
    },
    {
        id: "business-content-day",
        name: "Business Content Day",
        starting: "Starting at $1,800",
        timing: "Up to 3 hours",
        bestFor: "Brands, teams, restaurants, wellness businesses, studios, and companies that need a larger visual content library.",
        positioning: "A focused production day that builds the visual library your business will actually use.",
        includes: [
            "Creative direction",
            "Up to 3 hours of production",
            "Founder / team portraits",
            "Lifestyle brand imagery",
            "Product, space, or service visuals",
            "Website and social media content",
            "Optional photo + video",
            "Professionally edited gallery",
            "60+ edited images included",
        ],
        cta: "Plan a Content Day",
        sessionType: "Business Content Day",
    },
    {
        id: "event-coverage",
        name: "Event Coverage Package",
        starting: "From $700",
        timing: "2 hours",
        bestFor: "Conferences, conventions, brand launches, sponsor activations, and hotel events that need to keep producing value long after the day ends.",
        positioning: "Built for events that still need to perform across press, social, and sponsor recap weeks later.",
        includes: [
            "Editorial event coverage",
            "60+ polished images",
            "30–60 second highlight reel",
            "Same‑week turnaround for press and social",
            "Sponsor and speaker selects",
            "Tiered scaling: $1,500 — half day with short recap video",
            "$2,800 — full day with recap reel and same‑day press selects",
            "Multi‑event or annual packages available",
        ],
        cta: "Request Event Coverage",
        sessionType: "Event Coverage",
    },
    {
        id: "hospitality-content-day",
        name: "Hospitality Content Day",
        starting: "From $1,200",
        timing: "Half day",
        bestFor: "Hotels, resorts, restaurants, and hospitality venues selling summer occupancy, group bookings, and seasonal packages.",
        positioning: "Photo + short‑form video for rooms, pool, spa, dining, events, and local‑experience storytelling — built for marketing teams.",
        includes: [
            "Rooms, suites, and interiors",
            "Pool, spa, dining, and amenities",
            "Local experiences and lifestyle imagery",
            "Photo + short‑form video clips",
            "Content for socials, ads, email, and direct‑booking pages",
            "Tiered scaling: $2,200 — full day",
            "Multi‑day or seasonal retainer available",
            "Optimized for hospitality marketing teams",
        ],
        cta: "Plan a Hospitality Content Day",
        sessionType: "Hospitality Content",
    },
];

// ----- CUSTOM BUSINESS SOLUTIONS (smaller, secondary) -----
export const CUSTOM_BUSINESS_SOLUTIONS = [
    {
        id: "team-headshots",
        name: "Team Headshots",
        starting: "Starting at $950",
        bestFor: "Companies and organizations that need consistent, professional portraits.",
        cta: "Request a Team Quote",
        sessionType: "Team Headshots",
    },
    {
        id: "content-support",
        name: "Content Support / Retainers",
        starting: "Starting at $750/month",
        bestFor: "Businesses that need recurring content instead of one‑time shoots.",
        cta: "Ask About Content Support",
        sessionType: "Brand Story Session",
    },
];

// ----- BRAND & BUSINESS MOTION & CONTENT ADD-ONS -----
export const BRAND_BUSINESS_ADDONS = [
    {
        id: "short-reel",
        name: "Short‑Form Reel Add‑On",
        starting: "Starting at $300",
        bestFor: "Social media clips, behind‑the‑scenes, founder introductions, service highlights, and simple reels.",
        sessionType: "Photo + Video Content",
    },
    {
        id: "brand-video",
        name: "Brand Video Add‑On",
        starting: "Starting at $650",
        bestFor: "Website hero video, founder story, business overview, launch content, or campaign clips.",
        sessionType: "Photo + Video Content",
    },
    {
        id: "photo-video-link",
        name: "Photo + Video Content Session",
        starting: "Starting at $1,200",
        bestFor: "Clients who want a full photo + short‑form video session — see the main package above.",
        sessionType: "Photo + Video Content",
        isLinkBack: true,
    },
];

// ----- ATELIER (Fine-Art Prints) tier summary -----
export const ATELIER_TIERS = [
    { id: "prints", name: "Fine‑Art Prints", starting: "Starting at $175", note: "Signed editions on archival fine‑art paper." },
    { id: "framed", name: "Framed Prints", starting: "Starting at $450", note: "Walnut, oak, brass, or matte black framing." },
    { id: "albums", name: "Legacy Albums", starting: "Starting at $650", note: "Hand‑bound silk or linen heirloom albums." },
    { id: "wall-art", name: "Custom Wall Art Consultation", starting: "Starting at $750", note: "Designed for the room, the light, and the eye." },
];

// ----- THE TRAVEL ARCHIVE -----
// A curated fine-art print collection shaped by travel, landscape,
// architecture, light, and quiet human presence.
export const TRAVEL_ARCHIVE = [
    {
        id: "ta1",
        title: "Desert Field, Dusk",
        edition: "Edition of 6",
        collection: "Landscapes Without Witness",
        location: "Travel Archive",
        statement: "The desert just before it forgets the day.",
        image: LA("ta1"),
        startingAt: "$880",
    },
    {
        id: "ta2",
        title: "Mountain Light",
        edition: "Edition of 8",
        collection: "Mountain Studies",
        location: "Dagestan",
        statement: "Stone, dust, and the first warmth of the day.",
        image: LA("ta2"),
        startingAt: "$760",
    },
    {
        id: "ta3",
        title: "Tokyo, Quiet Hour",
        edition: "Edition of 10",
        collection: "Architecture in Repose",
        location: "Tokyo, Japan",
        statement: "Architecture composing itself against the city.",
        image: LA("ta3"),
        startingAt: "$720",
    },
    {
        id: "ta4",
        title: "Manhattan, Threshold",
        edition: "Edition of 8",
        collection: "Architecture in Repose",
        location: "New York City",
        statement: "A window holding the geometry of an evening.",
        image: LA("ta4"),
        startingAt: "$680",
    },
    {
        id: "ta5",
        title: "Quiet Figure",
        edition: "Edition of 6",
        collection: "Quiet Human Presence",
        location: "Travel",
        statement: "Someone moving through the architecture, almost a footnote.",
        image: LA("ta5"),
        startingAt: "$820",
    },
    {
        id: "ta6",
        title: "Northern Light",
        edition: "Edition of 6",
        collection: "Landscapes Without Witness",
        location: "Travel",
        statement: "Weather composing the day, frame by frame.",
        image: LA("ta6"),
        startingAt: "$880",
    },
];

export const TRAVEL_ARCHIVE_COPY =
    "Landscapes, cities, roads, mountains, water, and quiet architectural moments from the travel archive \u2014 fine\u2011art photographs created for homes, offices, collectors, and meaningful spaces.";

// ----- JOURNAL · FIELD NOTES (category copy) -----
export const JOURNAL_FIELD_NOTES_COPY =
    "Stories from behind the image \u2014 travel, light, creative process, location studies, and the observations that shape the work.";

// ----- MENTORING (Studio School) tiers -----
export const MENTORING_TIERS = [
    {
        id: "clarity",
        name: "Creative Clarity Session",
        starting: "Starting at $250",
        bestFor: "Quick direct guidance on creative direction, pricing, portfolio, or next business move.",
        format: "Single 60‑min private session",
    },
    {
        id: "audit",
        name: "Portfolio & Website Audit",
        starting: "Starting at $350",
        bestFor: "Photographers or creatives who need a strategic review of their portfolio, website, messaging, pricing, and client path.",
        format: "Async written review + 45‑min call",
    },
    {
        id: "creative-direction",
        name: "Creative Direction Mentoring",
        starting: "Starting at $650",
        badge: "Most Popular",
        bestFor: "Photographers who want to improve posing, storytelling, styling, lighting, and emotional quality of their work.",
        format: "4 × 90‑min private sessions",
    },
    {
        id: "business",
        name: "Photography Business Mentoring",
        starting: "Starting at $1,200",
        bestFor: "Photographers who want to raise prices, refine offers, attract better clients, and build a more profitable creative brand.",
        format: "6 × 60‑min private sessions",
    },
];

// Confirmation copy for inquiry form
export const INQUIRY_CONFIRMATION =
    "Thank you — your inquiry has been received. I’ll review your details and respond with availability, recommendations, and next steps.";


// ===========================================================
// BRAND & BUSINESS POSITIONING
// Practical, conversion-focused copy that speaks directly to
// founders, professionals, and service providers.
// ===========================================================

// Hero — replaces studio-page hero copy with stronger sales positioning
export const BUSINESS_HERO = {
    kicker: "Brand & Business Studio",
    headline: "Visuals That Make Your Business Look Trusted, Premium & Ready to Sell.",
    subhead:
        "Professional portraits, team headshots, brand imagery, event coverage, and hospitality content — plus photo + video — for founders, medical and hospitality brands, professional services, event organizers, and growing businesses.",
    ctaPrimary: "Build Your Brand Presence",
    ctaSecondary: "View Business Portfolio",
};

// "What Strong Business Visuals Should Do" — 5 strategic outcomes
export const BUSINESS_VISUAL_PROMISES = [
    {
        n: "01",
        title: "Build Trust Fast",
        copy: "Your images should make people feel safe choosing you before they ever speak with you.",
    },
    {
        n: "02",
        title: "Make You Look Premium",
        copy: "Polished visuals help your business feel more established, intentional, and higher value.",
    },
    {
        n: "03",
        title: "Show the Person Behind the Brand",
        copy: "Clients buy from people. Strong portraits make your business feel human and credible.",
    },
    {
        n: "04",
        title: "Support Your Website & Social Media",
        copy: "Create a content library you can actually use across your website, LinkedIn, Instagram, ads, email, and launches.",
    },
    {
        n: "05",
        title: "Help Clients Understand What You Offer",
        copy: "Visual storytelling shows your process, space, service, product, team, and client experience.",
    },
];

// "Who This Is For" — 10 clear buyer types
export const BUSINESS_BUYER_TYPES = [
    "Founders & entrepreneurs",
    "Coaches & consultants",
    "Doctors, wellness professionals & med spas",
    "Beauty, skincare & cosmetology professionals",
    "Real estate professionals",
    "Restaurants, studios & lifestyle brands",
    "Creative businesses & artists",
    "Local service businesses",
    "Corporate teams & organizations",
    "Personal brands & content creators",
];

// "What You Can Use the Images For" — practical use cases
export const BUSINESS_IMAGE_USE_CASES = [
    "Website homepage",
    "About page",
    "Services page",
    "LinkedIn",
    "Instagram",
    "Ads",
    "Email marketing",
    "Press features",
    "Speaker profiles",
    "Launch campaigns",
    "Google Business profile",
    "Sales pages",
    "Printed marketing materials",
];

// Guided experience reassurance bullets
export const BUSINESS_GUIDED_EXPERIENCE = {
    headline: "A Guided Business Content Experience",
    copy:
        "You do not need to know how to pose, what to shoot, or how to plan the entire session. Every Brand & Business session includes direction, planning, posing guidance, and a clear shot list so we create images that support your actual business goals.",
    bullets: [
        "Brand direction call included",
        "Shot list planning included",
        "Posing and expression guidance included",
        "Website and social media usage considered",
        "Photo + video options available",
        "Gallery delivered ready for marketing use",
    ],
};

// Closing CTA block used on Home + studio page bottom
export const BUSINESS_TRUST_CTA = {
    headline: "Your visuals should make people trust you faster.",
    copy:
        "Whether you are updating your website, launching a service, growing your personal brand, or refreshing your team presence, your images should help clients understand who you are, what you offer, and why they should choose you.",
    cta: "Build Your Brand Presence",
    sessionType: "Brand Story Session",
};

// Short "Brand & Business" card copy for the Home split section
export const BUSINESS_HOME_CARD = {
    name: "Brand & Business Studio",
    copy:
        "Professional portraits, brand imagery, team headshots, website content, social media visuals, and photo + video content for businesses that want to look trusted, polished, and ready to sell.",
    cta: "Explore Brand & Business",
};


// ─────────────────────────────────────────────────────────────────────────
// Business — Selected Work gallery
// Phase 5.4: moved out of BusinessArizona.jsx (was inline `GALLERY_ITEMS`).
// Canonical source: Sanity gallery doc slug `business-selected-work`.
// This array stays in `data.js` as the FE rollback fallback per
// Phase 5 §5 contract — if Sanity is unreachable the page still renders
// the same 17 items in the same order.
// ─────────────────────────────────────────────────────────────────────────
export const BUSINESS_GALLERY_ITEMS = [
    { id: "g1",  publicId: "3231-306_Corporate_26_ermdkt.jpg",                    alt: "Bright smiling business and personal-brand portrait — confident editorial photography for founders and consultants." },
    { id: "g2",  publicId: "04_Corporate_28_xx7y2a.jpg",                          alt: "Formal corporate portrait — refined business photography for executives and professional services." },
    { id: "g3",  publicId: "02_Corporate_19_sgdfng.jpg",                          alt: "Corporate relationship photography — team and partnership imagery for professional services and growing businesses." },
    { id: "g4",  publicId: "01_Corporate_24_nzlgwo.jpg",                          alt: "Polished corporate headshot — premium business photography for service providers and B2B brands." },
    { id: "g5",  publicId: "06_Corporate_25_ryvvp9.jpg",                          alt: "Black and white seated portrait — editorial founder photography with calm, premium presence." },
    { id: "g6",  publicId: "3061-343_Corporate_14_vl0ngj.jpg",                    alt: "Horizontal corporate detail photography — environmental business imagery for offices and studios." },
    { id: "g7",  publicId: "03_Corporate_13_o16cwq.jpg",                          alt: "Creative founder portrait on a bold yellow backdrop — editorial business photography for personal brands." },
    { id: "g8",  publicId: "3363-653_Corporate_9_kwj8fm.jpg",                     alt: "Confident editorial business portrait — refined imagery for entrepreneurs and creative leaders." },
    { id: "g9",  publicId: "3831-343_Corporate_1_cdckah.jpg",                     alt: "Approachable personal-brand portrait — warm editorial photography for founders and consultants." },
    { id: "g10", publicId: "Business_portfolio_evs-1_qpkkux.jpg",                 alt: "Black and white founder portrait — editorial business photography for personal brands." },
    { id: "g11", publicId: "05_Corporate_10_jsr2ba.jpg",                          alt: "Clean professional headshot — premium business photography for executives and service providers." },
    { id: "g12", publicId: "Business_portfolio_evs-2_o6h085.jpg",                 alt: "Brand and founder photography — horizontal editorial business imagery for website and marketing." },
    { id: "g13", publicId: "6364-336_Corporate_6_jghese.jpg",                     alt: "Salmon blazer editorial portrait — bold business photography for creative founders and personal brands." },
    { id: "g14", publicId: "3832-323_Corporate_8_wqlfip.jpg",                     alt: "Black and white corporate portrait — timeless executive photography." },
    { id: "g15", publicId: "3036-316_Corporate_23_tftw0g.jpg",                    alt: "Formal corporate portrait — confident business imagery for professional services." },
    { id: "g16", publicId: "3632-646_Corporate_18_or8zqh.jpg",                    alt: "Seated business portrait — editorial corporate photography for consultants and founders." },
    { id: "g17", publicId: "_____Businesscreative_portfolio_evs-7_kykjzs.jpg",    alt: "Creative founder brand imagery — content for entrepreneurs building a strong visual presence." },
];

// ─────────────────────────────────────────────────────────────────────────
// Home-Services — Selected Work gallery (SWR fallback)
// New campaign landing page `/home-services-photography` reads the Sanity
// gallery doc slug `home-services-selected-work`. That doc is seeded from
// `business-selected-work` (deep-copy with new keys) by the seed script
// at /app/sanity-studio/scripts/seed-phase15-galleries.mjs so Eli can
// curate home-services stills independently of /business without
// affecting it.
//
// Until the Sanity doc exists (or briefly during initial fetch), this
// fallback keeps the section from rendering empty. Same items as
// BUSINESS_GALLERY_ITEMS (intentional — Eli will swap to trades-
// appropriate photos later); `id`s rekeyed `hs1..hs17` so React
// reconciliation doesn't share keys with /business when navigating
// between the two pages in the same session.
// ─────────────────────────────────────────────────────────────────────────
export const HOME_SERVICES_GALLERY_ITEMS = BUSINESS_GALLERY_ITEMS.map(
    (item, idx) => ({ ...item, id: `hs${idx + 1}` })
);
